import { useCallback, useRef, useState } from 'react';
import { useLocale } from '@hooks/useLocale';
import { triggerAnalytics } from '@utils/analytics/triggerAnalytics';
import { trackAlgoliaAddToCart } from '@utils/algolia/analytics';
import { trackAddToCartEvent, GA4_EVENT_NAMES } from '@utils/gtm/ga4';
import { getGenderFromString } from '@utils/gtm/ga4/utils';
import { albyTrackAddToCart } from '@utils/alby';
import { FEATURE_FLAG_ALBY_CHATBOT } from '@config/feature-flag';
import { CART_COLOUR, CART_SIZE } from '@adapters/bigcommerce/constants';
import { ATC_SOURCES, AtcSource } from '@utils/cart/atcSource';
import type {
  Cart,
  CartMessage,
  ItemBody,
  OptionSelections,
} from '@adapters/bigcommerce/types/cart';
import type {
  MecProduct,
  MecVariant,
} from '@adapters/bigcommerce/types/product';
import useAddItem from './useAddItem';

const { ADD_TO_CART, QUICK_ADD } = GA4_EVENT_NAMES;

/**
 * Add-to-cart gesture for Quick Add. Wraps {@link useAddItem} and owns request
 * state + analytics parity — but not success UX. PLP renders inline feedback and
 * passes nothing; PDP can later pass `onSuccess` (e.g. `openCartFlyout`).
 *
 * SPU/STS store-pickup cookie logic from `AddToCartCTA` is intentionally out of
 * scope: Quick Add is a ship-to-home-default gesture and
 * `CartContext.validateCartItems` reconciles.
 */

export type QuickAddStatus = 'idle' | 'adding' | 'success' | 'error';

export interface QuickAddAnalyticsContext {
  source: 'plp' | 'search' | 'pdp' | 'hotspot';
  /** Algolia `hit.__queryID` for conversion attribution. */
  queryID?: string;
  /**
   * Category names, root first (e.g. `product.categories` from the quick-add
   * projection). Feeds the GA4 `item_category` path.
   */
  categories?: string[];
  categoryId?: string;
  /**
   * The originating PLP URL (path + query, no origin) the add fired from.
   * Feeds the GA4 `add_to_cart` event's `item_list_name` for revenue
   * attribution back to the PLP (ED-460). Undefined for non-PLP sources.
   * `source: 'hotspot'` overrides this to the constant `'Hotspot'` instead
   * of a URL (ED-447) — the Image Hotspot banner isn't itself a PLP, so
   * attributing the add to "whatever page it happened to render on" would
   * conflate a merch banner with a real product list.
   */
  itemListName?: string;
  /** The Image Hotspot tab's co-op brand, `null` when unauthored (ED-447).
      Undefined for every other `source`. */
  coOpBrand?: string | null;
}

/**
 * GA4 `atc_source` (ED-555) for each Quick Add surface. Exhaustive on purpose, so a
 * new `source` has to pick a value. `'pdp'` is the PDP Why Choose chips (ED-357).
 */
const ATC_SOURCE_BY_QUICK_ADD_SOURCE: Record<
  QuickAddAnalyticsContext['source'],
  AtcSource
> = {
  plp: ATC_SOURCES.PLP_QUICK_ADD,
  search: ATC_SOURCES.PLP_QUICK_ADD,
  hotspot: ATC_SOURCES.HOTSPOT,
  pdp: ATC_SOURCES.PDP_WHY_CHOOSE,
};

export interface QuickAddInput {
  product: Pick<
    MecProduct,
    | 'entityId'
    | 'gaName'
    | 'colourOptionId'
    | 'sizeOptionId'
    | 'isGiftCard'
    | 'minPurchaseQuantity'
    | 'code'
    | 'brand'
  >;
  /** Must have `entityId` (numeric size-level variant id). */
  variant: MecVariant;
  colour: { id: number; name: string };
  /** Omit for one-size products. */
  size?: { id: number; name: string | null };
  /** Defaults to `max(1, minPurchaseQuantity ?? 1)`. */
  quantity?: number;
  analytics: QuickAddAnalyticsContext;
}

export interface QuickAddErrorContext {
  attemptedToAdd: number;
  added: number;
}

export interface UseQuickAddToCartResult {
  addToCart: (
    input: QuickAddInput
  ) => Promise<{ ok: boolean; messages?: CartMessage[] }>;
  status: QuickAddStatus;
  /** Popover-local error key/message; never routed to cart-level CartMessages UI. */
  error: string | null;
  /** Populated only alongside `'quickAdd.error.insufficientStock'` — the
      requested/available counts for interpolating the cart's own message copy. */
  errorContext: QuickAddErrorContext | null;
  reset: () => void;
}

export function useQuickAddToCart(options?: {
  onSuccess?: (input: QuickAddInput) => void;
}): UseQuickAddToCartResult {
  const locale = useLocale();
  const onSuccess = options?.onSuccess;

  const [status, setStatus] = useState<QuickAddStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [errorContext, setErrorContext] = useState<QuickAddErrorContext | null>(
    null
  );

  // `useAddItem`'s callback resolves `void`, so capture the returned cart and the
  // per-call analytics context via refs for post-add inspection.
  const lastCartRef = useRef<Cart>(null);
  const analyticsCtxRef = useRef<QuickAddAnalyticsContext | null>(null);

  const addItem = useAddItem(({ data, item }) => {
    lastCartRef.current = data;
    const ctx = analyticsCtxRef.current;
    triggerAnalytics({
      data,
      item,
      categories: ctx?.categories,
      categoryId: ctx?.categoryId,
      // Quick add confirms in its own popover/sheet and never opens the
      // mini cart, so don't report a `view_cart` that didn't happen.
      trackMiniCartView: false,
    });
  });

  const reset = useCallback(() => {
    setStatus('idle');
    setError(null);
    setErrorContext(null);
  }, []);

  const addToCart = useCallback(
    async (
      input: QuickAddInput
    ): Promise<{ ok: boolean; messages?: CartMessage[] }> => {
      const { product, variant, colour, size, quantity, analytics } = input;

      setErrorContext(null);

      // Guards — return an error, never throw to the UI.
      if (product.isGiftCard) {
        setStatus('error');
        setError('quickAdd.error.giftCard');
        return { ok: false };
      }
      if (!variant?.entityId) {
        setStatus('error');
        setError('quickAdd.error.missingVariant');
        return { ok: false };
      }
      if (!product.colourOptionId) {
        setStatus('error');
        setError('quickAdd.error.missingColourOption');
        return { ok: false };
      }

      const resolvedQuantity = Math.max(
        1,
        quantity ?? product.minPurchaseQuantity ?? 1
      );

      const optionSelections: OptionSelections[] = [
        {
          option_id: product.colourOptionId,
          option_value: `${colour.id}`,
          name: CART_COLOUR,
          value: `${colour.name}`,
        },
        ...(product.sizeOptionId && size
          ? [
              {
                option_id: product.sizeOptionId,
                option_value: `${size.id}`,
                name: CART_SIZE,
                value: size.name ?? '',
              },
            ]
          : []),
      ];

      const itemBody: ItemBody = {
        productName: product.gaName,
        productId: product.entityId,
        variantId: variant.entityId,
        quantity: resolvedQuantity,
        optionSelections,
      };

      setStatus('adding');
      setError(null);
      analyticsCtxRef.current = analytics;
      lastCartRef.current = null;

      try {
        const atcSource = ATC_SOURCE_BY_QUICK_ADD_SOURCE[analytics.source];
        await addItem(itemBody, atcSource);

        // Cast to bypass control-flow narrowing from the `= null` reset above;
        // the cart is populated by `useAddItem`'s callback during the await.
        const cart = lastCartRef.current as Cart;

        // BigCommerce caps a request that exceeds remaining stock instead of
        // rejecting it outright — the response still comes back as a plain
        // success, with the shortfall attached to the matching line item's
        // own `errorMessages` (`updateCartWithAvailableQuantity`), not to
        // `cart.cartMessages`. Left unchecked, that's invisible to the
        // cart-level check below and this hook would report `success` even
        // though nothing (or less than requested) was actually added.
        const insufficientStockMessage =
          cart?.cartData?.line_items?.physical_items
            ?.find((lineItem) => lineItem.variant_id === variant.entityId)
            ?.errorMessages?.find(
              (message) => message.key === 'insufficientQuantity'
            );

        if (insufficientStockMessage) {
          setStatus('error');
          setError('quickAdd.error.insufficientStock');
          setErrorContext({
            attemptedToAdd:
              insufficientStockMessage.attemptedToAdd ?? resolvedQuantity,
            added: insufficientStockMessage.added ?? 0,
          });
          return { ok: false, messages: [insufficientStockMessage] };
        }

        const errorMessages = (cart?.cartMessages ?? []).filter(
          (message: CartMessage) => message.type === 'ERROR'
        );

        if (errorMessages.length > 0) {
          setStatus('error');
          setError(errorMessages[0].key ?? 'quickAdd.error.generic');
          return { ok: false, messages: errorMessages };
        }

        // Post-add analytics parity (slimmed from AddToCartCTA). PDP-only fields
        // (breadcrumbs, erpCategory, ...) are omitted; GA4 utils tolerate them.
        trackAlgoliaAddToCart({
          variantDataForAlgolia: {
            sku: variant.sku || '',
            price: variant.price || null,
          },
          quantity: resolvedQuantity,
          locale,
        });

        // `item_category` is derived from breadcrumbs, of which only `name`
        // feeds `createCategoryPath` — synthesize them from the category names.
        const categoryBreadcrumbs = analytics.categories?.length
          ? analytics.categories.map((name) => ({
              entityId: 0,
              name,
              path: '',
            }))
          : undefined;

        trackAddToCartEvent({
          eventName: ADD_TO_CART,
          item: {
            brand: product.brand ? { name: product.brand.name } : null,
            gaName: product.gaName,
            price: variant.price,
            selectedVariantSku: variant.sku,
            productCode: product.code ?? '',
            quantity: resolvedQuantity,
            selectedColour: { name: colour.name },
            selectedSize: size ? { name: size.name } : undefined,
            priceType: variant.price?.priceType,
            gender: getGenderFromString(product.gaName),
            inventoryAggregated: variant.inventoryAggregated ?? null,
            isMarketplacer: variant.isMarketplace,
            breadcrumbs: categoryBreadcrumbs,
            atcSource,
          },
          extraAttributes: {
            cart_type: QUICK_ADD,
            add_to_cart_source: analytics.source,
            item_list_name: analytics.itemListName,
            coop_brand: analytics.coOpBrand,
          },
        });

        if (locale === 'en' && FEATURE_FLAG_ALBY_CHATBOT) {
          albyTrackAddToCart(
            variant.sku,
            variant.price?.price,
            resolvedQuantity
          );
        }

        setStatus('success');
        onSuccess?.(input);
        return { ok: true, messages: cart?.cartMessages ?? undefined };
      } catch (err) {
        console.error('Quick add to cart failed:', err);
        setStatus('error');
        setError('quickAdd.error.generic');
        return { ok: false };
      }
    },
    [addItem, locale, onSuccess]
  );

  return { addToCart, status, error, errorContext, reset };
}

export default useQuickAddToCart;
